import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, Copy, RefreshCw, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const DEPTS = [["sala", "Sala"], ["bar", "Bar"], ["cucina", "Cucina"]];
const DEPT_LABEL = Object.fromEntries(DEPTS);
const inp = "bg-[#0F172A] border-slate-700 text-white h-9";

export const StaffPanel = () => {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", dept: "sala", phone: "" });
  const [calToken, setCalToken] = useState("");

  useEffect(() => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/staff-calendar-link").then((r) => setCalToken(r.data.token)).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome");
    try {
      const r = await api.post("/admin/staff", form);
      setStaff([...staff, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ name: "", dept: form.dept, phone: "" });
      toast.success("Aggiunto al personale");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tablesLink = calToken ? `${window.location.origin}/tavoli/${calToken}` : "";

  return (
    <>
      <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-3">Personale (per i turni)</h3>
        <div className="flex gap-2 flex-wrap mb-3">
          <Input data-testid="staff-name-input" className={`${inp} flex-1 min-w-[140px]`} placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="staff-dept-select" value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })}
            className="bg-[#0F172A] border border-slate-700 rounded-md h-9 text-sm px-2">
            {DEPTS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
          </select>
          <Input data-testid="staff-phone-input" className={`${inp} w-36`} placeholder="Telefono (opz.)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Button data-testid="staff-add-button" className="bg-[#C85A32] hover:bg-[#a94a28] h-9" onClick={add}><UserPlus className="w-4 h-4 mr-1" /> Aggiungi</Button>
        </div>
        <div className="space-y-1">
          {staff.map((s) => (
            <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center gap-2 text-sm py-1.5 border-b border-slate-700/50">
              <span className="font-semibold">{s.name}</span>
              <span className="text-[10px] uppercase font-bold text-[#F0B429]">{DEPT_LABEL[s.dept]}</span>
              {s.phone && <span className="text-xs text-slate-400">{s.phone}</span>}
              <button data-testid={`staff-delete-${s.id}`} onClick={async () => { await api.delete(`/admin/staff/${s.id}`); setStaff(staff.filter((x) => x.id !== s.id)); }}
                className="ml-auto text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {!staff.length && <p className="text-sm text-slate-500">Nessun dipendente salvato. Aggiungili qui: li ritroverai come suggerimenti nei turni.</p>}
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-2">Calendario prenotazioni (per te e il personale)</h3>
        <p className="text-xs text-slate-400 mb-3">Vista rapida sincronizzata con le prenotazioni tavoli (orari, coperti, telefoni, conferma WhatsApp). Condividila con il personale di sala: si salva come icona sullo smartphone, senza PIN.</p>
        <div className="flex gap-2 flex-wrap items-center">
          <code data-testid="tables-cal-link" className="text-xs bg-[#0F172A] border border-slate-700 rounded-md px-2.5 py-2 flex-1 min-w-[200px] truncate">{tablesLink || "…"}</code>
          <Button data-testid="tables-cal-copy" variant="outline" size="sm" className="border-slate-600 text-slate-900" onClick={() => { navigator.clipboard.writeText(tablesLink); toast.success("Link copiato"); }}>
            <Copy className="w-4 h-4 mr-1" /> Copia
          </Button>
          <Button data-testid="staff-cal-regen" variant="outline" size="sm" className="border-slate-600 text-slate-900" onClick={async () => {
            if (!window.confirm("Rigenerare il link? Quello vecchio smetterà di funzionare per tutti quelli a cui l'hai condiviso.")) return;
            const r = await api.post("/admin/staff-calendar-link/regenerate");
            setCalToken(r.data.token);
            toast.success("Nuovo link generato");
          }}><RefreshCw className="w-4 h-4 mr-1" /> Rigenera</Button>
        </div>
      </div>
    </>
  );
};
